import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Multi-region disaster-recovery controller (part 90).
 *
 * The page registers regions with RTO/RPO objectives, sets one drilling cadence per region in
 * Asia/Bangkok, records measured drill outcomes and shows the promotion gate for every region.
 * The API never executes scripts/dr-drill.sh and never starts a cluster: scheduled runs are
 * simulated work items, and simulated evidence cannot authorise a promotion.
 *
 * This page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type RegionState = 'ready' | 'degraded' | 'stale' | 'blocked' | 'unproven' | 'pending' | 'disabled'
type RunStatus = 'queued' | 'passed' | 'failed'
type Cadence = 'daily' | 'weekly' | 'monthly'

interface DRRegion {
  id: string
  code: string
  name_th: string
  role: 'primary' | 'standby'
  rto_minutes: number
  rpo_minutes: number
  window_hours: number
  enabled: boolean
  created_at: string
  updated_at: string
}

interface DRSchedule {
  id: string
  region_id: string
  cadence: Cadence
  at_hour: number
  at_minute: number
  weekday: number
  day_of_month: number
  enabled: boolean
  timezone: string
  next_due_at: string
  last_enqueued_at?: string
}

interface DRRunRef {
  id: string
  status: RunStatus
  trigger: 'manual' | 'scheduled'
  simulated: boolean
  created_at: string
  finished_at?: string
  rto_seconds: number
  rpo_seconds: number
}

interface DRCheck {
  id: string
  title: string
  status: 'pass' | 'warn' | 'fail'
  detail: string
  remediation: string
  blocking: boolean
}

interface DRGate {
  region_id: string
  region_code: string
  name_th: string
  state: RegionState
  state_label: string
  allow: boolean
  degraded: boolean
  score: number
  reason_th: string
  blockers: string[]
  warnings: string[]
  window_hours: number
  evidence_age_hours: number
  evidence_expires_at?: string
  last_run?: DRRunRef
  last_pass?: DRRunRef
  evaluated_at: string
}

interface DRRegionReadiness {
  region: DRRegion
  schedule?: DRSchedule
  gate: DRGate
}

interface DRGateSummary {
  total: number
  ready: number
  degraded: number
  stale: number
  blocked: number
  unproven: number
  pending: number
  disabled: number
  allow: number
  attention: number
}

interface DRReadiness {
  generated_at: string
  program_ready: boolean
  summary: DRGateSummary
  regions: DRRegionReadiness[]
  script_path: string
  note_th: string
}

interface DRRun {
  id: string
  region_id: string
  schedule_id?: string
  trigger: 'manual' | 'scheduled'
  status: RunStatus
  script_path: string
  simulated: boolean
  due_at?: string
  started_at?: string
  finished_at?: string
  rto_seconds: number
  rpo_seconds: number
  rto_objective_minutes: number
  rpo_objective_minutes: number
  steps_planned: number
  steps_passed: number
  steps_failed: number
  integrity_verified: boolean
  checks_total: number
  checks_passed: number
  checks_warned: number
  checks_failed: number
  checks: DRCheck[]
  blockers: string[]
  warnings: string[]
  summary_th: string
  notes: string
  created_at: string
  completed_at?: string
}

interface DueResult {
  runs: DRRun[]
  count: number
  note_th: string
}

interface RegionForm {
  code: string
  name_th: string
  role: 'primary' | 'standby'
  rto_minutes: number
  rpo_minutes: number
  window_hours: number
  enabled: boolean
}

interface ScheduleForm {
  region_id: string
  cadence: Cadence
  at_hour: number
  at_minute: number
  weekday: number
  day_of_month: number
  enabled: boolean
}

interface MeasurementForm {
  region_id: string
  run_id: string
  rto_minutes: number
  rpo_minutes: number
  steps_planned: number
  steps_passed: number
  steps_failed: number
  integrity_verified: boolean
  simulated: boolean
  notes: string
}

const READINESS_KEY = ['dr-readiness']
const RUNS_KEY = ['dr-runs']

const WEEKDAYS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function stateClass(state: RegionState): string {
  if (state === 'ready') return 'badge badge-closed'
  if (state === 'degraded') return 'badge badge-reviewing'
  if (state === 'stale' || state === 'blocked') return 'badge badge-received'
  return 'chip'
}

function runClass(status: RunStatus): string {
  if (status === 'passed') return 'badge badge-closed'
  if (status === 'failed') return 'badge badge-received'
  return 'badge badge-reviewing'
}

function runLabel(status: RunStatus): string {
  if (status === 'passed') return 'ผ่าน'
  if (status === 'failed') return 'ไม่ผ่าน'
  return 'รอผล'
}

function cadenceLabel(cadence: Cadence): string {
  if (cadence === 'daily') return 'ทุกวัน'
  if (cadence === 'weekly') return 'ทุกสัปดาห์'
  return 'ทุกเดือน'
}

function secondsLabel(seconds: number): string {
  if (seconds < 60) return `${number(seconds)} วินาที`
  return `${(seconds / 60).toFixed(1)} นาที`
}

export function DRControl() {
  const qc = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [regionForm, setRegionForm] = useState<RegionForm>({
    code: 'ap-southeast-7',
    name_th: 'ศูนย์สำรอง กรุงเทพฯ',
    role: 'standby',
    rto_minutes: 60,
    rpo_minutes: 15,
    window_hours: 24,
    enabled: true,
  })
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>({
    region_id: '',
    cadence: 'weekly',
    at_hour: 9,
    at_minute: 0,
    weekday: 1,
    day_of_month: 1,
    enabled: true,
  })
  const [measure, setMeasure] = useState<MeasurementForm>({
    region_id: '',
    run_id: '',
    rto_minutes: 25,
    rpo_minutes: 3,
    steps_planned: 6,
    steps_passed: 6,
    steps_failed: 0,
    integrity_verified: true,
    simulated: true,
    notes: '',
  })

  const readinessQ = useQuery({
    queryKey: READINESS_KEY,
    queryFn: () => api<DRReadiness>('/v1/dr/readiness'),
    staleTime: 15_000,
  })
  const runsQ = useQuery({
    queryKey: RUNS_KEY,
    queryFn: () => api<DRRun[]>('/v1/dr/runs?limit=50'),
    staleTime: 10_000,
  })

  const regions = readinessQ.data?.regions ?? []
  const runs = runsQ.data ?? []
  const queuedRuns = runs.filter((run) => run.status === 'queued')

  useEffect(() => {
    if (!readinessQ.data || readinessQ.data.regions.length === 0) return
    const first = readinessQ.data.regions[0].region.id
    setScheduleForm((current) => (current.region_id ? current : { ...current, region_id: first }))
    setMeasure((current) => (current.region_id ? current : { ...current, region_id: first }))
  }, [readinessQ.data])

  useEffect(() => {
    if (measure.run_id || queuedRuns.length === 0) return
    setMeasure((current) => ({ ...current, run_id: queuedRuns[0].id }))
  }, [measure.run_id, queuedRuns])

  function refresh() {
    void qc.invalidateQueries({ queryKey: READINESS_KEY })
    void qc.invalidateQueries({ queryKey: RUNS_KEY })
  }

  const registerRegion = useMutation({
    mutationFn: (body: RegionForm) => apiWrite<{ region: DRRegion }>('/v1/dr/regions', 'POST', body),
    onSuccess: (result) => {
      setNotice(`บันทึกภูมิภาค ${result.region.code} แล้ว`)
      refresh()
    },
    onError: (err) => setNotice(errorText(err, 'บันทึกภูมิภาคไม่สำเร็จ')),
  })

  const setSchedule = useMutation({
    mutationFn: (body: ScheduleForm) =>
      apiWrite<{ schedule: DRSchedule }>(`/v1/dr/regions/${body.region_id}/schedule`, 'POST', {
        cadence: body.cadence,
        at_hour: body.at_hour,
        at_minute: body.at_minute,
        weekday: body.weekday,
        day_of_month: body.day_of_month,
        enabled: body.enabled,
      }),
    onSuccess: (result) => {
      setNotice(`ตั้งกำหนดการแล้ว · รอบถัดไป ${thaiDateTime(result.schedule.next_due_at)}`)
      refresh()
    },
    onError: (err) => setNotice(errorText(err, 'ตั้งกำหนดการไม่สำเร็จ')),
  })

  const runDrill = useMutation({
    mutationFn: (body: MeasurementForm) =>
      apiWrite<{ run: DRRun; note_th: string }>('/v1/dr/runs', 'POST', {
        region_id: body.region_id,
        simulated: body.simulated,
        rto_seconds: Math.round(body.rto_minutes * 60),
        rpo_seconds: Math.round(body.rpo_minutes * 60),
        steps_planned: body.steps_planned,
        steps_passed: body.steps_passed,
        steps_failed: body.steps_failed,
        integrity_verified: body.integrity_verified,
        notes: body.notes,
      }),
    onSuccess: (result) => {
      setNotice(
        `บันทึกผลการซ้อมแล้ว · ${runLabel(result.run.status)} · ${result.run.simulated ? 'ข้อมูลจำลอง' : 'ผลจริง'} · ${result.note_th}`,
      )
      refresh()
    },
    onError: (err) => setNotice(errorText(err, 'บันทึกผลการซ้อมไม่สำเร็จ')),
  })

  const completeRun = useMutation({
    mutationFn: (body: MeasurementForm) =>
      apiWrite<{ run: DRRun }>(`/v1/dr/runs/${body.run_id}/complete`, 'POST', {
        rto_seconds: Math.round(body.rto_minutes * 60),
        rpo_seconds: Math.round(body.rpo_minutes * 60),
        steps_planned: body.steps_planned,
        steps_passed: body.steps_passed,
        steps_failed: body.steps_failed,
        integrity_verified: body.integrity_verified,
        notes: body.notes,
      }),
    onSuccess: (result) => {
      setNotice(`บันทึกผลการซ้อมตามกำหนดการแล้ว · ${runLabel(result.run.status)}`)
      refresh()
    },
    onError: (err) => setNotice(errorText(err, 'บันทึกผลการซ้อมตามกำหนดการไม่สำเร็จ')),
  })

  const dueTick = useMutation({
    mutationFn: () => apiWrite<DueResult>('/v1/dr/due', 'POST', {}),
    onSuccess: (result) => {
      setNotice(
        result.count === 0
          ? 'ยังไม่มีกำหนดการที่ครบกำหนด'
          : `สร้างรายการซ้อมจากการครบกำหนด ${result.count} รายการ · ${result.note_th}`,
      )
      refresh()
    },
    onError: (err) => setNotice(errorText(err, 'ตรวจกำหนดการไม่สำเร็จ')),
  })

  function regionCode(regionID: string): string {
    return regions.find((view) => view.region.id === regionID)?.region.code ?? regionID.slice(0, 8)
  }

  function submitSchedule() {
    if (!scheduleForm.region_id) {
      setNotice('เลือกภูมิภาคก่อนตั้งกำหนดการ')
      return
    }
    setSchedule.mutate(scheduleForm)
  }

  function submitManualRun() {
    if (!measure.region_id) {
      setNotice('เลือกภูมิภาคก่อนบันทึกผลการซ้อม')
      return
    }
    runDrill.mutate(measure)
  }

  function submitCompletion() {
    if (!measure.run_id) {
      setNotice('เลือกรายการซ้อมที่รอผลก่อนบันทึกผล')
      return
    }
    completeRun.mutate(measure)
  }

  const head = (
    <PageHead
      title="ศูนย์ควบคุมการกู้คืนภัยพิบัติ"
      sub="กำหนดการซ้อมหลายภูมิภาค · เป้าหมาย RTO/RPO · ประตูการเลื่อนระดับจากหลักฐานที่ยังไม่หมดอายุ"
      actions={
        <button
          className="btn"
          onClick={() => {
            refresh()
            void readinessQ.refetch()
            void runsQ.refetch()
          }}
        >
          รีเฟรช
        </button>
      }
    />
  )

  if (readinessQ.isLoading) {
    return (
      <>
        {head}
        <Skel h={120} />
        <div style={{ height: 16 }} />
        <Skel h={260} />
      </>
    )
  }

  if (readinessQ.isError || !readinessQ.data) {
    return (
      <>
        {head}
        <Empty
          title="โหลดศูนย์ควบคุม DR ไม่ได้"
          body="ตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์ และตรวจว่าไมเกรชัน 0126 ถูกนำไปใช้กับฐานข้อมูลนี้แล้ว"
        />
      </>
    )
  }

  const readiness = readinessQ.data
  const summary = readiness.summary

  return (
    <>
      {head}

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        <Stat
          label="ความพร้อมของโครงการ"
          value={readiness.program_ready ? 'พร้อม' : 'ยังไม่พร้อม'}
          foot={readiness.program_ready ? 'ทุกภูมิภาคมีหลักฐานจริงภายในหน้าต่าง' : 'มีภูมิภาคที่ต้องแก้ไขก่อนเลื่อนระดับ'}
        />
        <Stat label="ภูมิภาคที่ลงทะเบียน" value={number(summary.total)} unit="ภูมิภาค" foot={`เปิดใช้งาน ${summary.total - summary.disabled}`} />
        <Stat label="อนุญาตให้เลื่อนระดับ" value={number(summary.allow)} unit="ภูมิภาค" foot={`พร้อม ${summary.ready} · ใกล้หมดอายุ ${summary.degraded}`} />
        <Stat
          label="ต้องดำเนินการ"
          value={number(summary.attention)}
          unit="ภูมิภาค"
          foot={`เก่า ${summary.stale} · ถูกบล็อก ${summary.blocked} · ไม่มีหลักฐาน ${summary.unproven}`}
        />
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">ประตูการเลื่อนระดับ</div>
            <div className="dim" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.8 }}>{readiness.note_th}</div>
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
              สคริปต์อ้างอิง {readiness.script_path} · ประเมินเมื่อ {thaiDateTime(readiness.generated_at)}
            </div>
          </div>
          <span className={readiness.program_ready ? 'badge badge-closed' : 'badge badge-received'}>
            {readiness.program_ready ? 'พร้อมทั้งโครงการ' : 'ยังไม่พร้อมทั้งโครงการ'}
          </span>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          <span className="chip">รวม {summary.total}</span>
          <span className="chip">พร้อม {summary.ready}</span>
          <span className="chip">ใกล้หมดอายุ {summary.degraded}</span>
          <span className="chip">เก่าเกินกำหนด {summary.stale}</span>
          <span className="chip">ถูกบล็อก {summary.blocked}</span>
          <span className="chip">ไม่มีหลักฐาน {summary.unproven}</span>
          <span className="chip">รอผล {summary.pending}</span>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="card-label">สถานะรายภูมิภาค</div>
        {regions.length === 0 ? (
          <p className="dim" style={{ fontSize: 12.5, marginTop: 10 }}>
            ยังไม่มีภูมิภาค ลงทะเบียนภูมิภาคแรกด้านล่างก่อนตั้งกำหนดการซ้อม
          </p>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>ภูมิภาค</th>
                  <th style={{ width: 170 }}>สถานะ</th>
                  <th style={{ width: 150 }}>หลักฐานล่าสุด</th>
                  <th style={{ width: 170 }}>กำหนดซ้อมถัดไป</th>
                  <th>เหตุผลของประตู</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((view) => (
                  <tr key={view.region.id} style={{ cursor: 'default' }}>
                    <td>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <strong>{view.region.code}</strong>
                        <span className="chip">{view.region.role === 'primary' ? 'primary' : 'standby'}</span>
                        {view.region.enabled ? null : <span className="chip">ปิดใช้งาน</span>}
                      </div>
                      <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>{view.region.name_th}</div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                        RTO {number(view.region.rto_minutes)} นาที · RPO {number(view.region.rpo_minutes)} นาที · หน้าต่าง {number(view.region.window_hours)} ชั่วโมง
                      </div>
                    </td>
                    <td>
                      <span className={stateClass(view.gate.state)}>{view.gate.state_label}</span>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>คะแนน {number(view.gate.score)}/100</div>
                    </td>
                    <td>
                      {view.gate.last_pass?.finished_at ? (
                        <>
                          <div style={{ fontSize: 12.5 }}>{thaiDateTime(view.gate.last_pass.finished_at)}</div>
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                            อายุ {view.gate.evidence_age_hours.toFixed(1)} ชม. · ใช้ได้ถึง {view.gate.evidence_expires_at ? thaiDateTime(view.gate.evidence_expires_at) : '—'}
                          </div>
                        </>
                      ) : (
                        <span className="dim" style={{ fontSize: 12 }}>ยังไม่มีผลที่ผ่าน</span>
                      )}
                    </td>
                    <td>
                      {view.schedule ? (
                        <>
                          <div style={{ fontSize: 12.5 }}>{cadenceLabel(view.schedule.cadence)} · {String(view.schedule.at_hour).padStart(2, '0')}:{String(view.schedule.at_minute).padStart(2, '0')}</div>
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                            {view.schedule.next_due_at ? thaiDateTime(view.schedule.next_due_at) : 'ยังไม่ตั้งกำหนด'}
                          </div>
                        </>
                      ) : (
                        <span className="dim" style={{ fontSize: 12 }}>ยังไม่ตั้งกำหนดการ</span>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>{view.gate.reason_th}</div>
                      {view.gate.blockers.length > 0 ? (
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>ข้อขัดขวาง: {view.gate.blockers.join(' · ')}</div>
                      ) : null}
                      {view.gate.warnings.length > 0 ? (
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>ข้อควรระวัง: {view.gate.warnings.join(' · ')}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid-2" style={{ marginBottom: 22 }}>
        <div className="card card-pad">
          <div className="card-label">ลงทะเบียนภูมิภาค</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            รหัสเดิมจะอัปเดตภูมิภาคนั้น เป้าหมายใหม่มีผลกับการซ้อมครั้งถัดไป ส่วนผลที่บันทึกแล้วยังคงเป้าหมายเดิมที่ใช้ตัดสิน
          </p>
          <div className="field">
            <label htmlFor="dr-code">รหัสภูมิภาค</label>
            <input id="dr-code" className="input" value={regionForm.code} onChange={(event) => setRegionForm({ ...regionForm, code: event.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="dr-name">ชื่อที่แสดง</label>
            <input id="dr-name" className="input" value={regionForm.name_th} onChange={(event) => setRegionForm({ ...regionForm, name_th: event.target.value })} />
          </div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-role">บทบาท</label>
              <select id="dr-role" className="select" value={regionForm.role} onChange={(event) => setRegionForm({ ...regionForm, role: event.target.value as RegionForm['role'] })}>
                <option value="standby">standby</option>
                <option value="primary">primary</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-window">หน้าต่างหลักฐาน (ชั่วโมง)</label>
              <input id="dr-window" className="input" type="number" min={1} max={720} value={regionForm.window_hours} onChange={(event) => setRegionForm({ ...regionForm, window_hours: Number(event.target.value) })} />
            </div>
          </div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-rto">เป้าหมาย RTO (นาที)</label>
              <input id="dr-rto" className="input" type="number" min={1} max={1440} value={regionForm.rto_minutes} onChange={(event) => setRegionForm({ ...regionForm, rto_minutes: Number(event.target.value) })} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-rpo">เป้าหมาย RPO (นาที)</label>
              <input id="dr-rpo" className="input" type="number" min={0} max={1440} value={regionForm.rpo_minutes} onChange={(event) => setRegionForm({ ...regionForm, rpo_minutes: Number(event.target.value) })} />
            </div>
          </div>
          <label className="row" style={{ gap: 6, fontSize: 12.5, marginTop: 8 }}>
            <input type="checkbox" checked={regionForm.enabled} onChange={(event) => setRegionForm({ ...regionForm, enabled: event.target.checked })} />
            เปิดใช้งานภูมิภาคนี้
          </label>
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={registerRegion.isPending} onClick={() => registerRegion.mutate(regionForm)}>
              บันทึกภูมิภาค
            </button>
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-label">กำหนดการซ้อม</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            คำนวณในเขตเวลา Asia/Bangkok · หนึ่งภูมิภาคมีกำหนดการเดียว และรอบถัดไปคำนวณจากเวลาปัจจุบันเมื่อบันทึก
          </p>
          <div className="field">
            <label htmlFor="dr-schedule-region">ภูมิภาค</label>
            <select id="dr-schedule-region" className="select" value={scheduleForm.region_id} onChange={(event) => setScheduleForm({ ...scheduleForm, region_id: event.target.value })}>
              <option value="">เลือกภูมิภาค</option>
              {regions.map((view) => (
                <option key={view.region.id} value={view.region.id}>{view.region.code} · {view.region.name_th}</option>
              ))}
            </select>
          </div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-cadence">ความถี่</label>
              <select id="dr-cadence" className="select" value={scheduleForm.cadence} onChange={(event) => setScheduleForm({ ...scheduleForm, cadence: event.target.value as Cadence })}>
                <option value="daily">ทุกวัน</option>
                <option value="weekly">ทุกสัปดาห์</option>
                <option value="monthly">ทุกเดือน</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-hour">เวลา (ชั่วโมง)</label>
              <input id="dr-hour" className="input" type="number" min={0} max={23} value={scheduleForm.at_hour} onChange={(event) => setScheduleForm({ ...scheduleForm, at_hour: Number(event.target.value) })} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dr-minute">นาที</label>
              <input id="dr-minute" className="input" type="number" min={0} max={59} value={scheduleForm.at_minute} onChange={(event) => setScheduleForm({ ...scheduleForm, at_minute: Number(event.target.value) })} />
            </div>
          </div>
          {scheduleForm.cadence === 'weekly' ? (
            <div className="field">
              <label htmlFor="dr-weekday">วันในสัปดาห์</label>
              <select id="dr-weekday" className="select" value={scheduleForm.weekday} onChange={(event) => setScheduleForm({ ...scheduleForm, weekday: Number(event.target.value) })}>
                {WEEKDAYS.map((day, index) => (
                  <option key={day} value={index}>{day}</option>
                ))}
              </select>
            </div>
          ) : null}
          {scheduleForm.cadence === 'monthly' ? (
            <div className="field">
              <label htmlFor="dr-day">วันที่ (1-28)</label>
              <input id="dr-day" className="input" type="number" min={1} max={28} value={scheduleForm.day_of_month} onChange={(event) => setScheduleForm({ ...scheduleForm, day_of_month: Number(event.target.value) })} />
            </div>
          ) : null}
          <label className="row" style={{ gap: 6, fontSize: 12.5, marginTop: 8 }}>
            <input type="checkbox" checked={scheduleForm.enabled} onChange={(event) => setScheduleForm({ ...scheduleForm, enabled: event.target.checked })} />
            เปิดใช้งานกำหนดการ
          </label>
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={setSchedule.isPending} onClick={submitSchedule}>
              บันทึกกำหนดการ
            </button>
            <button className="btn" disabled={dueTick.isPending} onClick={() => dueTick.mutate()}>
              ตรวจรอบที่ครบกำหนด
            </button>
          </div>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="card-label">บันทึกผลการซ้อม</div>
        <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
          API บันทึกค่าที่วัดได้และอ้างอิง {readiness.script_path} แต่ไม่ได้รันสคริปต์หรือสร้างคลัสเตอร์ใหม่ ·
          ผลจากกำหนดการเป็นการจำลองและไม่สามารถปลดล็อกประตูเลื่อนระดับได้ ต้องบันทึกผลจริงที่ไม่ได้ทำเครื่องหมายจำลอง
        </p>
        <div className="grid-2" style={{ marginTop: 16 }}>
          <div>
            <div className="field">
              <label htmlFor="dr-run-region">ภูมิภาค</label>
              <select id="dr-run-region" className="select" value={measure.region_id} onChange={(event) => setMeasure({ ...measure, region_id: event.target.value })}>
                <option value="">เลือกภูมิภาค</option>
                {regions.map((view) => (
                  <option key={view.region.id} value={view.region.id}>{view.region.code} · {view.region.name_th}</option>
                ))}
              </select>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="dr-measure-rto">RTO ที่วัดได้ (นาที)</label>
                <input id="dr-measure-rto" className="input" type="number" min={0} value={measure.rto_minutes} onChange={(event) => setMeasure({ ...measure, rto_minutes: Number(event.target.value) })} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="dr-measure-rpo">RPO ที่วัดได้ (นาที)</label>
                <input id="dr-measure-rpo" className="input" type="number" min={0} value={measure.rpo_minutes} onChange={(event) => setMeasure({ ...measure, rpo_minutes: Number(event.target.value) })} />
              </div>
            </div>
            <label className="row" style={{ gap: 6, fontSize: 12.5, marginTop: 8 }}>
              <input type="checkbox" checked={measure.simulated} onChange={(event) => setMeasure({ ...measure, simulated: event.target.checked })} />
              ผลนี้เป็นการจำลอง (simulated=true) และใช้ยืนยันการเลื่อนระดับไม่ได้
            </label>
          </div>
          <div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="dr-steps-planned">ขั้นตอนทั้งหมด</label>
                <input id="dr-steps-planned" className="input" type="number" min={1} max={64} value={measure.steps_planned} onChange={(event) => setMeasure({ ...measure, steps_planned: Number(event.target.value) })} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="dr-steps-passed">ขั้นตอนที่สำเร็จ</label>
                <input id="dr-steps-passed" className="input" type="number" min={0} max={64} value={measure.steps_passed} onChange={(event) => setMeasure({ ...measure, steps_passed: Number(event.target.value) })} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="dr-steps-failed">ขั้นตอนที่ล้มเหลว</label>
                <input id="dr-steps-failed" className="input" type="number" min={0} max={64} value={measure.steps_failed} onChange={(event) => setMeasure({ ...measure, steps_failed: Number(event.target.value) })} />
              </div>
            </div>
            <label className="row" style={{ gap: 6, fontSize: 12.5, marginTop: 8 }}>
              <input type="checkbox" checked={measure.integrity_verified} onChange={(event) => setMeasure({ ...measure, integrity_verified: event.target.checked })} />
              ตรวจ checksum ของแถวทดสอบบนปลายทางแล้ว
            </label>
            <div className="field">
              <label htmlFor="dr-notes">บันทึก</label>
              <input id="dr-notes" className="input" value={measure.notes} onChange={(event) => setMeasure({ ...measure, notes: event.target.value })} />
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 10, marginTop: 14 }}>
          <button className="btn btn-primary" disabled={runDrill.isPending} onClick={submitManualRun}>
            บันทึกผลการซ้อม
          </button>
        </div>
      </div>

      {queuedRuns.length > 0 ? (
        <div className="card card-pad" style={{ marginBottom: 22 }}>
          <div className="card-label">รายการที่รอผล</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            รายการเหล่านี้ถูกสร้างเมื่อกำหนดการครบกำหนดและยังไม่มีการวัดผล การบันทึกผลจะใช้เป้าหมายที่ถูกบันทึกไว้ตอนสร้างรายการ
          </p>
          <div className="field">
            <label htmlFor="dr-complete-run">รายการซ้อม</label>
            <select id="dr-complete-run" className="select" value={measure.run_id} onChange={(event) => setMeasure({ ...measure, run_id: event.target.value })}>
              {queuedRuns.map((run) => (
                <option key={run.id} value={run.id}>
                  {regionCode(run.region_id)} · {run.due_at ? thaiDateTime(run.due_at) : thaiDateTime(run.created_at)} · {run.script_path}
                </option>
              ))}
            </select>
          </div>
          <div className="row" style={{ gap: 10, marginTop: 10, alignItems: 'center' }}>
            <button className="btn btn-primary" disabled={completeRun.isPending} onClick={submitCompletion}>
              บันทึกผลรายการที่เลือก
            </button>
            <span className="dim" style={{ fontSize: 12 }}>ใช้ค่าที่กรอกในแบบฟอร์มบันทึกผลด้านบน</span>
          </div>
        </div>
      ) : null}

      <div className="card card-pad">
        <div className="card-label">ประวัติการซ้อม</div>
        {runsQ.isError ? (
          <p className="dim" style={{ fontSize: 12.5, marginTop: 10 }}>โหลดประวัติการซ้อมไม่ได้</p>
        ) : runs.length === 0 ? (
          <p className="dim" style={{ fontSize: 12.5, marginTop: 10 }}>ยังไม่มีประวัติการซ้อม</p>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>เมื่อ</th>
                  <th>ภูมิภาค</th>
                  <th style={{ width: 110 }}>ที่มา</th>
                  <th style={{ width: 110 }}>ผล</th>
                  <th style={{ width: 130 }}>RTO / RPO</th>
                  <th style={{ width: 130 }}>ขั้นตอน</th>
                  <th>หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} style={{ cursor: 'default' }}>
                    <td>{thaiDateTime(run.finished_at ?? run.created_at)}</td>
                    <td>
                      <div>{regionCode(run.region_id)}</div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{run.script_path}</div>
                    </td>
                    <td>
                      <span className="chip">{run.trigger === 'scheduled' ? 'ตามกำหนดการ' : 'manual'}</span>
                      {run.simulated ? <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>จำลอง</div> : null}
                    </td>
                    <td><span className={runClass(run.status)}>{runLabel(run.status)}</span></td>
                    <td>
                      <div style={{ fontSize: 12 }}>{secondsLabel(run.rto_seconds)} / {secondsLabel(run.rpo_seconds)}</div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>เป้า {number(run.rto_objective_minutes)} / {number(run.rpo_objective_minutes)} นาที</div>
                    </td>
                    <td>{run.steps_passed}/{run.steps_planned}{run.steps_failed > 0 ? ` · ล้มเหลว ${run.steps_failed}` : ''}</td>
                    <td style={{ fontSize: 12 }}>{run.summary_th || run.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
